import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/context/FeatureFlagContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import PushPromptBanner from "@/components/PushPromptBanner";
import {
  LayoutDashboard, Users, UserRound, GraduationCap, Music2, Coins, Settings2,
  Building2, CalendarDays, CalendarCheck, Receipt, Wallet, ClipboardCheck,
  MessageSquareText, BadgeCheck, BarChart3, LogOut, Menu, X, Sun, Clock,
  RefreshCcw, Banknote, TrendingUp, UserCog, Bell, Baby, Inbox, Gauge, Send, Plane,
  Award, Trophy, Lightbulb, Rocket, Globe, Star, Megaphone, BookOpen, HelpCircle,
  MessagesSquare, AlertTriangle, DatabaseZap, UserPlus, Search, LayoutGrid, CalendarClock, PartyPopper,
  ShieldCheck, KeyRound,
} from "lucide-react";

const ADMIN_NAV = [
  { section: "Overview", items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/front-desk", label: "Front Desk", icon: Receipt },
  ]},
  { section: "People", items: [
    { to: "/admin/students", label: "Students", icon: GraduationCap },
    { to: "/admin/parents", label: "Parents", icon: UserRound },
    { to: "/admin/teachers", label: "Teachers", icon: Users },
    { to: "/admin/teaching-requests", label: "Teaching Requests", icon: GraduationCap },
    { to: "/admin/availability-requests", label: "Availability Requests", icon: Clock },
    { to: "/admin/walk-in-enrol", label: "Walk-in Enrol", icon: UserPlus },
    { to: "/admin/find-slot", label: "Find Slot · Waitlist", icon: Search },
  ]},
  { section: "Setup", items: [
    { to: "/admin/courses", label: "Music Courses", icon: Music2 },
    { to: "/admin/fee-settings", label: "Fee & SST Settings", icon: Settings2 },
    { to: "/admin/studios", label: "Studios", icon: Building2 },
    { to: "/admin/studio-reservations", label: "Studio Reservations", icon: CalendarClock },
    { to: "/admin/school-holidays", label: "School Holidays", icon: PartyPopper },
  ]},
  { section: "Scheduling", items: [
    { to: "/admin/schedules", label: "Class Schedules", icon: CalendarDays },
    { to: "/admin/bookings", label: "Bookings & Calendar", icon: CalendarCheck },
  ]},
  { section: "Finance", items: [
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
    { to: "/admin/payments", label: "Payments", icon: Wallet },
    { to: "/admin/payroll", label: "Teacher Payroll", icon: Banknote },
  ]},
  { section: "Operations", items: [
    { to: "/admin/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/admin/lesson-remarks", label: "Lesson Remarks", icon: MessageSquareText },
    { to: "/admin/reschedule-requests", label: "Reschedule Requests", icon: RefreshCcw },
    { to: "/admin/leave-reschedule", label: "Leave Control Centre", icon: ClipboardCheck },
    { to: "/admin/reschedule-policy", label: "Reschedule Policy", icon: Gauge },
    { to: "/admin/leave-requests", label: "Leave Requests", icon: Plane },
    { to: "/admin/notification-center", label: "Notifications", icon: Send },
    { to: "/admin/approvals", label: "Approvals", icon: BadgeCheck },
    { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/data-import", label: "Data Import", icon: DatabaseZap },
    { to: "/admin/account-claims", label: "Account Claims", icon: KeyRound },
  ]},
  { section: "Performance", items: [
    { to: "/admin/teacher-kpi", label: "Teacher KPI", icon: TrendingUp },
    { to: "/admin/recommendations", label: "Recommendations", icon: Lightbulb },
    { to: "/admin/kpi-settings", label: "KPI Settings", icon: Settings2 },
  ]},
  { section: "Growth", items: [
    { to: "/admin/growth", label: "Growth Dashboard", icon: Rocket },
    { to: "/admin/growth/teacher-profiles", label: "Public Profiles", icon: Globe },
    { to: "/admin/growth/testimonials", label: "Testimonials", icon: Star },
    { to: "/admin/growth/enquiries", label: "Leads & Enquiries", icon: Megaphone, testId: "nav-growth-enquiries" },
    { to: "/admin/growth/enrolments", label: "Online Enrolments", icon: ClipboardCheck },
    { to: "/admin/growth/ai-knowledge", label: "AI Knowledge", icon: BookOpen },
    { to: "/admin/growth/ai-faqs", label: "AI FAQs", icon: HelpCircle },
    { to: "/admin/growth/ai-conversations", label: "AI Conversations", icon: MessagesSquare },
    { to: "/admin/growth/ai-escalations", label: "AI Escalations", icon: AlertTriangle },
  ]},
  { section: "System", superAdminOnly: true, items: [
    { to: "/admin/feature-control", label: "Feature Control", icon: ShieldCheck },
    { to: "/admin/whatsapp-test-centre", label: "WhatsApp Test Centre", icon: MessagesSquare },
    { to: "/admin/site-settings", label: "Site Settings", icon: Settings2 },
  ]},
];

const FINANCE_NAV = [
  { section: "Finance", items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
    { to: "/admin/payments", label: "Payments", icon: Wallet },
    { to: "/admin/payroll", label: "Teacher Payroll", icon: Banknote },
    { to: "/admin/teacher-kpi", label: "Teacher KPI", icon: TrendingUp },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  ]},
];

const TEACHER_NAV = [
  { section: "Teaching", items: [
    { to: "/teacher/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/teacher/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/teacher/today", label: "Today's Classes", icon: Sun },
    { to: "/teacher/timetable", label: "Timetable", icon: CalendarDays },
    { to: "/teacher/students", label: "My Students", icon: GraduationCap },
    { to: "/teacher/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/teacher/lesson-remarks", label: "Lesson Remarks", icon: MessageSquareText },
    { to: "/teacher/reschedule", label: "Reschedule", icon: RefreshCcw },
    { to: "/teacher/replacement-assistant", label: "Replacement Assistant", icon: CalendarClock },
    { to: "/teacher/leave", label: "Leave", icon: Plane },
    { to: "/teacher/availability", label: "Availability", icon: Clock },
    { to: "/teacher/teaching-requests", label: "Course Requests", icon: GraduationCap },
  ]},
  { section: "My Account", items: [
    { to: "/teacher/payroll", label: "My Payroll", icon: Banknote },
    { to: "/teacher/kpi", label: "My KPI", icon: TrendingUp },
    { to: "/teacher/rewards", label: "Rewards & Badges", icon: Award },
    { to: "/teacher/ranking", label: "Ranking", icon: Trophy },
    { to: "/teacher/profile", label: "Profile", icon: UserCog },
  ]},
];

const PARENT_NAV = [
  { section: "My Family", items: [
    { to: "/parent/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/parent/children", label: "My Children", icon: Baby },
    { to: "/parent/timetable", label: "Timetable", icon: CalendarDays },
    { to: "/parent/attendance", label: "Attendance", icon: ClipboardCheck },
    { to: "/parent/lesson-remarks", label: "Lesson Remarks", icon: MessageSquareText },
  ]},
  { section: "Billing", items: [
    { to: "/parent/invoices", label: "Invoices", icon: Receipt },
    { to: "/parent/payment-status", label: "Payment Status", icon: Wallet },
  ]},
  { section: "Requests", items: [
    { to: "/parent/reschedule", label: "Reschedule", icon: RefreshCcw },
    { to: "/parent/leave", label: "Leave", icon: Plane },
    { to: "/parent/notifications", label: "Notifications", icon: Bell },
  ]},
];

const ROLE_LABEL = {
  super_admin: "Super Admin", admin: "Admin", finance: "Finance",
  teacher: "Teacher", parent: "Parent", student: "Student",
};

/* Mobile bottom navigation — teacher/parent/student PWA quick tabs.
   Hamburger menu still exposes the full navigation. */
const BOTTOM_NAV = {
  teacher: [
    { to: "/teacher/dashboard", label: "Home", icon: LayoutDashboard },
    { to: "/teacher/today", label: "Today", icon: Sun },
    { to: "/teacher/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/teacher/reschedule", label: "Reschedule", icon: RefreshCcw },
    { to: "/teacher/leave", label: "Leave", icon: Plane },
  ],
  parent: [
    { to: "/parent/dashboard", label: "Home", icon: LayoutDashboard },
    { to: "/parent/timetable", label: "Timetable", icon: CalendarDays },
    { to: "/parent/reschedule", label: "Reschedule", icon: RefreshCcw },
    { to: "/parent/invoices", label: "Invoices", icon: Receipt },
    { to: "/parent/leave", label: "Leave", icon: Plane },
  ],
};
BOTTOM_NAV.student = BOTTOM_NAV.parent;

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const { isNavHidden } = useFeatureFlags();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const rawNav =
    user.role === "teacher" ? TEACHER_NAV :
    user.role === "parent" ? PARENT_NAV :
    user.role === "finance" ? FINANCE_NAV : ADMIN_NAV;

  // Filter: (1) drop superAdminOnly sections for non-super_admin,
  //         (2) drop items disabled by Super Admin Feature Control.
  const nav = rawNav
    .filter((group) => !group.superAdminOnly || user.role === "super_admin")
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !isNavHidden(item.to)),
    }))
    .filter((group) => group.items.length > 0);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Section icons for a calmer visual anchor per group
  const SECTION_ICON = {
    Overview: LayoutDashboard,
    People: Users,
    Setup: Settings2,
    Scheduling: CalendarDays,
    Finance: Coins,
    Operations: RefreshCcw,
    Growth: TrendingUp,
    "Super Admin": ShieldCheck,
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const NavContent = ({ dark = false }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
      {nav.map((group) => {
        const SIcon = SECTION_ICON[group.section] || LayoutGrid;
        return (
          <div key={group.section}>
            <div className={`px-3 mb-1 flex items-center gap-2 ${dark ? "text-primary-foreground/50" : "text-[color:var(--v2-text-muted)]"}`}>
              <SIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
              <p className="text-[10px] font-bold uppercase tracking-[0.18em]">{group.section}</p>
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId || `nav-${item.to.split("/").pop()}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => {
                    if (dark) {
                      return `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? "bg-primary-foreground/15 text-primary-foreground"
                          : "text-primary-foreground/65 hover:text-primary-foreground hover:bg-primary-foreground/8"
                      }`;
                    }
                    return `relative flex items-center gap-2.5 pl-3 pr-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-[color:var(--v2-light-blue)] text-[color:var(--v2-primary)]"
                        : "text-[color:var(--v2-text)] hover:bg-[color:var(--v2-soft-grey)]"
                    }`;
                  }}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && !dark && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-[color:var(--v2-primary)]" />
                      )}
                      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      <span className="truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );

  const initials = ((user.full_name || user.email || "U")
    .split(" ").map(p => p[0]).slice(0, 2).join("")).toUpperCase();

  return (
    <div className="min-h-screen bg-[color:var(--v2-soft-grey)]">
      {/* Desktop sidebar — v2 white, calm, purple accents */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-[color:var(--v2-border)] flex-col z-40">
        <div className="px-5 py-5 border-b border-[color:var(--v2-border)] flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[color:var(--v2-primary)] to-[color:var(--v2-primary-dark)] flex items-center justify-center shrink-0 shadow-[0_4px_10px_rgba(91,42,134,0.25)]">
            <Music2 className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="font-display text-[15px] font-semibold text-[color:var(--v2-text)] leading-tight truncate">Cornerstone</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--v2-primary)] font-semibold">Music School</p>
          </div>
        </div>
        <NavContent />
        <div className="p-3 border-t border-[color:var(--v2-border)]">
          <div className="flex items-center gap-3 rounded-xl bg-[color:var(--v2-soft-grey)] p-3">
            <div className="h-9 w-9 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center shrink-0">
              <span className="text-[13px] font-semibold text-[color:var(--v2-primary)]">{initials}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-[color:var(--v2-text)] truncate">{user.full_name || user.email}</p>
              <p className="text-[11px] text-[color:var(--v2-text-muted)] truncate">{ROLE_LABEL[user.role] || user.role}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-button"
              title="Sign out"
              className="h-8 w-8 rounded-lg flex items-center justify-center text-[color:var(--v2-text-muted)] hover:text-[color:var(--v2-danger)] hover:bg-white transition-colors"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[color:var(--v2-border)] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[color:var(--v2-primary)] to-[color:var(--v2-primary-dark)] flex items-center justify-center">
            <Music2 className="h-4 w-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="font-display font-semibold leading-none text-[color:var(--v2-text)]">Cornerstone</p>
            <p className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--v2-primary)] font-semibold">Music School</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white flex flex-col pt-16" data-testid="mobile-nav">
          <NavContent />
          <div className="p-4 border-t border-[color:var(--v2-border)]">
            <Button variant="outline" size="sm" data-testid="mobile-logout-button"
              className="w-full border-[color:var(--v2-border)] text-[color:var(--v2-text)]"
              onClick={handleLogout}>
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out ({user.full_name || user.email})
            </Button>
          </div>
        </div>
      )}

      {/* Single notification bell instance for all viewports */}
      <div className="fixed top-2.5 right-14 lg:top-5 lg:right-8 z-[60]">
        <NotificationBell />
      </div>

      <main className="lg:pl-64">
        <div className={`px-4 sm:px-8 py-8 max-w-[1400px] ${BOTTOM_NAV[user.role] ? "pb-24 lg:pb-8" : ""}`}>{children}</div>
      </main>

      <PushPromptBanner />

      {/* Mobile bottom navigation (teacher / parent / student) */}
      {BOTTOM_NAV[user.role] && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-[200] bg-primary border-t border-primary-foreground/10 flex justify-around pb-[env(safe-area-inset-bottom)]"
          data-testid="bottom-nav">
          {BOTTOM_NAV[user.role].map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`bottomnav-${item.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2.5 px-2 min-w-[56px] text-[10px] font-semibold transition-colors ${
                  isActive ? "text-secondary" : "text-primary-foreground/55"
                }`
              }>
              <item.icon className="h-5 w-5" strokeWidth={1.8} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
